import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';

interface Group {
  id: number;
  name: string;
}

export interface User {
  id?: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  password?: string;
  groups: number[];
  is_active: boolean;
}

interface UserFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  userToEdit?: User | null;
}

const UserForm: React.FC<UserFormProps> = ({ onSuccess, onCancel, userToEdit }) => {
  const { addToast } = useToast();
  const [username, setUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await api.get('groups/');
        setGroups(response.data);
      } catch (error) {
        console.error('Failed to fetch groups', error);
      }
    };
    fetchGroups();
  }, []);

  useEffect(() => {
    if (userToEdit) {
      setUsername(userToEdit.username);
      setFirstName(userToEdit.first_name);
      setLastName(userToEdit.last_name);
      setEmail(userToEdit.email);
      setSelectedGroups(userToEdit.groups || []);
      setIsActive(userToEdit.is_active);
      setPassword(''); // Don't show password
    }
  }, [userToEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const userData: any = {
      username,
      first_name: firstName,
      last_name: lastName,
      email,
      groups: selectedGroups,
      is_active: isActive
    };

    if (password) userData.password = password;

    try {
      if (userToEdit && userToEdit.id) {
        await api.patch(`users/${userToEdit.id}/`, userData);
        addToast('Usuário atualizado com sucesso!', 'success');
      } else {
        await api.post('users/', userData);
        addToast('Novo usuário criado com sucesso!', 'success');
      }
      onSuccess();
    } catch (error: any) {
      const errorMsg = error.response?.data?.username?.[0] || 'Erro ao salvar usuário.';
      addToast(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const SectionTitle = ({ title, icon }: { title: string, icon: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', marginTop: '10px' }}>
      <span style={{ fontSize: '1.2rem' }}>{icon}</span>
      <h4 style={{ margin: 0, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--primary-blue)' }}>{title}</h4>
    </div>
  );

  return (
    <div className="card" style={{ maxWidth: '700px', margin: '0 auto' }}>
      <div style={{ marginBottom: '25px', borderBottom: '1px solid var(--border-color)', paddingBottom: '15px' }}>
        <h3 style={{ margin: 0 }}>{userToEdit ? '👤 Editar Perfil de Usuário' : '✨ Criar Novo Usuário'}</h3>
      </div>

      <form onSubmit={handleSubmit}>
        <SectionTitle title="Dados de Identificação" icon="🆔" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          <div className="form-group">
            <label>Nome</label>
            <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Ex: João" required />
          </div>
          <div className="form-group">
            <label>Sobrenome</label>
            <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Ex: Silva" required />
          </div>
        </div>

        <div className="form-group">
          <label>E-mail Corporativo</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@conectaterra.com.br" required />
        </div>

        <div style={{ marginTop: '30px' }}>
          <SectionTitle title="Credenciais de Acesso" icon="🔐" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          <div className="form-group">
            <label>Nome de Usuário (Login)</label>
            <input 
              type="text" 
              value={username} 
              onChange={(e) => setUsername(e.target.value)} 
              placeholder="ex: joao.silva"
              required 
              disabled={!!userToEdit}
            />
          </div>
          <div className="form-group">
            <label>{userToEdit ? 'Nova Senha (opcional)' : 'Senha'}</label>
            <input 
              type="password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              placeholder={userToEdit ? "Deixe em branco para manter" : "Mínimo 10 caracteres"}
              required={!userToEdit} 
            />
          </div>
        </div>

        <div style={{ marginTop: '20px' }}>
          <label style={{ fontWeight: '600', marginBottom: '10px', display: 'block' }}>Perfis de Acesso (Grupos)</label>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', 
            gap: '10px',
            background: 'var(--bg-body)',
            padding: '15px',
            borderRadius: '12px',
            border: '1px solid var(--border-color)'
          }}>
            {groups.map(group => (
              <label key={group.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  checked={selectedGroups.includes(group.id)}
                  onChange={(e) => {
                    if (e.target.checked) setSelectedGroups([...selectedGroups, group.id]);
                    else setSelectedGroups(selectedGroups.filter(id => id !== group.id));
                  }}
                  style={{ width: '18px', height: '18px' }}
                />
                {group.name}
              </label>
            ))}
          </div>
        </div>

        <div style={{ 
          marginTop: '40px', 
          paddingTop: '20px', 
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={isActive} 
              onChange={(e) => setIsActive(e.target.checked)}
              style={{ width: '20px', height: '20px' }}
            />
            <span style={{ fontWeight: '600', color: isActive ? 'var(--success)' : 'var(--danger)' }}>
              {isActive ? 'Usuário Ativo' : 'Usuário Bloqueado'}
            </span>
          </label>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={onCancel} className="btn btn-secondary">Cancelar</button>
            <button type="submit" className="btn" disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar Usuário'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default UserForm;
